"use client";

import { AnimatePresence, motion, useScroll } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";

import { ThemeToggle } from "@/components/ui/theme-toggle";
import { LanguageToggle } from "@/components/ui/language-toggle";
import { WatchNavMenu } from "@/components/home/watch-nav-menu";
import { cn } from "@/lib/utils";
import type { Locale } from "@/i18n/config";

type NavTranslations = {
  home: string;
  watch: string;
  watchLive: string;
  watchLiveDesc: string;
  watchWordOfWeek: string;
  watchWordOfWeekDesc: string;
  plan: string;
  destiny: string;
  testimony: string;
  years: string;
  youthAlive: string;
  canada: string;
  yaf: string;
};

const getNavItems = (locale: Locale, translations: NavTranslations) => [
  { name: translations.home, href: `/${locale}` },
  { name: translations.years, href: `/${locale}/years` },
  { name: translations.destiny, href: `/${locale}/academy` },
  { name: translations.testimony, href: `/${locale}/testimony` },
];

type MobileMenuTranslations = {
  theme: string;
  language: string;
  brandName: string;
  // Optional: some callers still pass a partial object; JSX falls back to English
  declaration?: string;
  yearTheme?: string;
  openDoors?: string;
};

type NavbarProps = {
  forceDark?: boolean;
  solid?: boolean;
  locale?: Locale;
  translations?: NavTranslations;
  mobileMenuTranslations?: MobileMenuTranslations;
};

export const Navbar = ({ forceDark = false, solid = false, locale = 'en', translations, mobileMenuTranslations }: NavbarProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { scrollY } = useScroll();
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Default translations for backwards compatibility
  const t: NavTranslations = translations || {
    home: "Home",
    watch: "Watch",
    watchLive: "Watch Live & Messages",
    watchLiveDesc: "Streams & messages",
    watchWordOfWeek: "Word of the Week",
    watchWordOfWeekDesc: "Weekly video archive",
    plan: "Plan",
    destiny: "Destiny",
    testimony: "Testimony",
    years: "Our Story",
    youthAlive: "YOUTHALIVE",
    canada: "CANADA",
    yaf: "YAF",
  };

  const mm: MobileMenuTranslations = mobileMenuTranslations || {
    theme: "Theme",
    language: "Language",
    brandName: "YOUTH ALIVE",
    declaration: "2026 Declaration",
    yearTheme: "Our Year of",
    openDoors: "Open Doors",
  };

  const NAV_ITEMS = getNavItems(locale, t);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
  }, []);

  useEffect(() => {
    return scrollY.on("change", (latest) => setIsScrolled(latest > 50));
  }, [scrollY]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  const isDark = (mounted && resolvedTheme === "dark") || forceDark;

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="navbar-root fixed top-4 md:top-8 left-0 w-full z-[100] px-3 md:px-6 transition-opacity duration-300"
    >
      <div
        className={cn(
          "max-w-7xl mx-auto rounded-3xl md:rounded-full transition-all duration-700 px-3 md:px-6 py-1.5 md:py-2 flex flex-col gap-2 border overflow-visible",
          isScrolled || solid
            ? (isDark ? "bg-black/80 border-white/10" : "bg-white/80 border-zinc-200") + " backdrop-blur-2xl shadow-lg"
            : "bg-transparent border-transparent"
        )}
      >
        <div className="w-full flex items-center">
          {/* Left Section: Desktop Logo / Mobile Hamburger */}
          <div className="flex-1 flex items-center justify-start">
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className={cn(
              "lg:hidden p-2 rounded-xl transition-colors",
              isDark ? "hover:bg-white/10" : "hover:bg-black/5"
            )}
            aria-label="Open menu"
          >
            <List weight="bold" className={cn("w-5 h-5", isDark ? "text-white" : "text-black")} />
          </button>
          <Link href={`/${locale}`} className="hidden lg:flex items-center gap-3 pl-2">
            <div className="relative w-9 h-9 xl:w-10 xl:h-10 rounded-xl overflow-hidden shadow-sm">
              <Image
                src="/new2025logo.png"
                width={40}
                height={40}
                alt="Logo"
                className="object-cover w-full h-full"
                priority
              />
            </div>
            <span className={cn(
              "font-display font-black text-base xl:text-lg tracking-tighter transition-colors",
              isDark ? "text-white" : "text-black"
            )}>
              {t.youthAlive}{" "}
              {/* #C41E3A is 3.1:1 on black and fails WCAG; use the dark accent red on dark surfaces */}
              <span className={cn("relative inline-block pr-2", isDark ? "text-[#FF4D4D]" : "text-brand-red")}>
                {t.canada}
                <span className="pointer-events-none absolute right-0 top-0 translate-x-[12%] -translate-y-[45%]">
                  <Image
                    src="/mapleleaf.png"
                    alt=""
                    width={24}
                    height={24}
                    className="w-6 h-6 rotate-12 drop-shadow-sm"
                    priority
                  />
                </span>
              </span>
            </span>
          </Link>
          </div>

          {/* Middle Section: Mobile Logo / Desktop Links */}
          <div className="flex-none lg:flex-1 flex items-center justify-center">
          {/* Mobile Logo + Text */}
          <Link href={`/${locale}`} className="lg:hidden flex items-center gap-2">
            <div className="relative w-7 h-7 rounded-lg overflow-hidden">
              <Image
                src="/new2025logo.png"
                width={28}
                height={28}
                alt="Logo"
                className="object-cover w-full h-full"
              />
            </div>
            <span className={cn(
              "font-display font-black text-[10px] tracking-tighter uppercase whitespace-nowrap transition-colors",
              isDark ? "text-white" : "text-black"
            )}>
              {t.yaf}{" "}
              <span className={cn("relative inline-block pr-2", isDark ? "text-[#FF4D4D]" : "text-brand-red")}>
                {t.canada}
                <span className="pointer-events-none absolute right-0 top-0 translate-x-[12%] -translate-y-[45%]">
                  <Image
                    src="/mapleleaf.png"
                    alt=""
                    width={20}
                    height={20}
                    className="w-5 h-5 rotate-12 drop-shadow-sm"
                    priority
                  />
                </span>
              </span>
            </span>
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.slice(0, 2).map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={cn(
                  "px-3 xl:px-6 py-2 rounded-full text-[10px] xl:text-[11px] font-bold uppercase tracking-widest transition-all",
                  isDark ? "hover:bg-white/10 text-white/80 hover:text-white" : "hover:bg-black/5 text-black hover:text-black/70"
                )}
              >
                {item.name}
              </Link>
            ))}
            <WatchNavMenu
              isDark={isDark}
              label={t.watch}
              liveLabel={t.watchLive}
              liveSublabel={t.watchLiveDesc}
              wordOfWeekLabel={t.watchWordOfWeek}
              wordOfWeekSublabel={t.watchWordOfWeekDesc}
              locale={locale}
            />
            {NAV_ITEMS.slice(2).map((item) => (
              <Link
                key={item.name}
                href={item.href}
                className={cn(
                  "px-3 xl:px-6 py-2 rounded-full text-[10px] xl:text-[11px] font-bold uppercase tracking-widest transition-all",
                  isDark ? "hover:bg-white/10 text-white/80 hover:text-white" : "hover:bg-black/5 text-black hover:text-black/70"
                )}
              >
                {item.name}
              </Link>
            ))}
          </div>
          </div>

          {/* Right Section: ThemeToggle + LanguageToggle (Desktop) */}
          <div className="flex-1 flex items-center justify-end gap-2">
            <div className="hidden lg:flex items-center gap-2 mr-2">
              <LanguageToggle locale={locale} />
              <ThemeToggle />
            </div>
          </div>
        </div>

      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[150] lg:hidden"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="absolute top-0 left-0 h-full w-[85%] max-w-sm bg-white dark:bg-zinc-950 shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-zinc-100 dark:border-white/10">
                <Link
                  href={`/${locale}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3"
                >
                  <div className="relative w-10 h-10 rounded-xl overflow-hidden">
                    <Image
                      src="/new2025logo.png"
                      width={40}
                      height={40}
                      alt="Logo"
                      className="object-cover w-full h-full"
                    />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-display font-black text-sm tracking-tight text-black dark:text-white">
                      {mm.brandName}
                    </span>
                    <span className="text-[10px] font-bold tracking-widest text-brand-red">
                      {t.canada}
                    </span>
                  </div>
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-xl bg-zinc-100 dark:bg-white/10 hover:bg-zinc-200 dark:hover:bg-white/20 transition-colors"
                  aria-label="Close menu"
                >
                  <X weight="bold" className="w-5 h-5 text-black dark:text-white" />
                </button>
              </div>

              {/* Navigation Links */}
              <nav className="p-6">
                <ul className="space-y-2">
                  <motion.li
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 }}
                    className="rounded-[1.6rem] border border-zinc-200/80 bg-zinc-50 px-4 py-4 dark:border-white/10 dark:bg-white/[0.03]"
                  >
                    <div className="px-1 pb-2 text-[10px] font-black uppercase tracking-[0.28em] text-zinc-500 dark:text-zinc-400">
                      {t.watch}
                    </div>
                    <div className="space-y-1">
                      <Link
                        href={`/${locale}/watch`}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-4 rounded-2xl px-3 py-3 text-base font-bold text-black transition-colors hover:bg-white dark:text-white dark:hover:bg-white/5"
                      >
                        <span className="w-2 h-2 rounded-full bg-brand-red" />
                        {t.watchLive}
                      </Link>
                      <Link
                        href={`/${locale}/word-of-the-week`}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-4 rounded-2xl px-3 py-3 text-base font-bold text-black transition-colors hover:bg-white dark:text-white dark:hover:bg-white/5"
                      >
                        <span className="w-2 h-2 rounded-full bg-brand-gold" />
                        {t.watchWordOfWeek}
                      </Link>
                    </div>
                  </motion.li>
                  {NAV_ITEMS.map((item, index) => (
                    <motion.li
                      key={item.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.14 + index * 0.05 }}
                    >
                      <Link
                        href={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-4 px-4 py-4 rounded-2xl text-lg font-bold text-black dark:text-white hover:bg-zinc-100 dark:hover:bg-white/5 transition-colors group"
                      >
                        <span className="w-2 h-2 rounded-full bg-brand-red opacity-0 group-hover:opacity-100 transition-opacity" />
                        {item.name}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </nav>

              {/* Year Theme */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="px-6 py-8"
              >
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-zinc-100 via-zinc-50 to-white dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-950 p-6 border border-zinc-200/50 dark:border-white/5">
                  {/* Decorative Elements */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-brand-gold/10 rounded-full blur-3xl" />
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-brand-red/10 rounded-full blur-2xl" />

                  <div className="relative">
                    <span className="text-[9px] font-black uppercase tracking-[0.4em] text-brand-red dark:text-brand-gold mb-3 block">
                      {mm.declaration ?? "2026 Declaration"}
                    </span>
                    <h3 className="font-serif text-2xl italic leading-tight text-black dark:text-white">
                      {mm.yearTheme ?? "Our Year of"}{" "}
                      <span className="relative inline-block">
                        <span className="relative z-10 text-brand-red">{mm.openDoors ?? "Open Doors"}</span>
                        <motion.span
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: 1 }}
                          transition={{ delay: 0.5, duration: 0.6, ease: "easeOut" }}
                          className="absolute bottom-1 left-0 right-0 h-2 bg-brand-gold/30 dark:bg-brand-gold/20 -z-0 origin-left"
                        />
                      </span>
                    </h3>
                  </div>
                </div>
              </motion.div>

              {/* Footer */}
              <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-zinc-100 dark:border-white/10">
                <div className="flex items-center">
                  <div className="flex flex-col gap-2">
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
                        {mm.theme}
                      </span>
                      <ThemeToggle />
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
                        {mm.language}
                      </span>
                      <LanguageToggle locale={locale} />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};
